import React from 'react';
import FollowupActions from './FollowupActions';
import { parseTimeSlot } from '../../domain/followupLogic';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Visas när kunden har föreslagit tider för uppföljning
 * Scenario C: CUSTOMER_REQUEST_WITH_TIMES
 */
const CustomerTimeSlots = ({
  note,
  slots,
  bookedFollowupSlot,
  bookingSlot,
  onBookSlot,
  onSuggestTime,
  onBookMeeting,
  onApproveForCustomer,
  onBookHomeVisit,
  approveLoading,
  isApproved = false,
  customerId,
}) => {
  const handleBookSlot = async (slot) => {
    try {
      onBookSlot.setBookingSlot(slot);
      const token = localStorage.getItem('token');

      if (!customerId) {
        onBookSlot.setBookingSlot('');
        onBookSlot.setResponseModalData({
          title: 'Kunde inte boka',
          message: 'Kund-ID saknas',
          type: 'error',
        });
        onBookSlot.setShowResponseModal(true);
        return;
      }

      // Parsa displaySlot till date och time
      const { date, time } = parseTimeSlot(slot);

      const response = await fetch(`${API_URL}/api/estimat/customer/${customerId}/followup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date,
          time,
          displaySlot: slot,
          notes: 'Bokning från kundens föreslagna tider i kundportalen',
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Bokning misslyckades');
      }

      onBookSlot.setBookedFollowupSlot(slot);
      if (data?.meeting?.teamsJoinUrl) {
        onBookSlot.setFollowupTeamsUrl(data.meeting.teamsJoinUrl);
      }

      onBookSlot.setResponseModalData({
        title: 'Uppföljning bokad',
        message: 'En Teams‑inbjudan har skickats till kund, beställare 2 och byggkonsult.',
        type: 'success',
        details: data?.meeting?.teamsJoinUrl
          ? {
              'Teams-länk': (
                <a
                  href={data.meeting.teamsJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 underline"
                >
                  Öppna
                </a>
              ),
            }
          : null,
      });
      onBookSlot.setShouldRefreshAfterModal(true);
      onBookSlot.setShowResponseModal(true);
    } catch (err) {
      console.error(err);
      onBookSlot.setResponseModalData({
        title: 'Kunde inte boka',
        message: err.message || 'Okänt fel',
        type: 'error',
      });
      onBookSlot.setShowResponseModal(true);
    } finally {
      onBookSlot.setBookingSlot('');
    }
  };

  // Filtrera slots om en är bokad (visa bara den bokade)
  const displaySlots = bookedFollowupSlot ? slots.filter((s) => s === bookedFollowupSlot) : slots;

  return (
    <div>
      {note && (
        <div className="text-sm text-gray-700 mb-3">
          <span className="font-medium">Kommentar:</span> {note}
        </div>
      )}

      {slots.length > 0 ? (
        <ul className="space-y-2 text-sm text-gray-700 mb-4">
          {displaySlots.map((slot) => {
            const isBooked = bookedFollowupSlot === slot;
            const isBooking = bookingSlot === slot;
            const isDisabled = bookedFollowupSlot && slot !== bookedFollowupSlot;

            return (
              <li
                key={slot}
                className={`flex items-center justify-between gap-4 bg-gray-50 border border-gray-200 rounded px-3 py-2 ${
                  isDisabled ? 'opacity-60' : ''
                }`}
              >
                <span className="text-gray-900">{slot}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={`text-xs px-3 py-1.5 rounded whitespace-nowrap flex items-center gap-2 ${
                      isBooked
                        ? 'bg-green-700 text-white opacity-80 cursor-not-allowed'
                        : isBooking
                        ? 'bg-green-600 text-white opacity-80 cursor-wait'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    title="Boka denna tid via Teams/Outlook"
                    onClick={() => handleBookSlot(slot)}
                    disabled={isBooked || isBooking}
                  >
                    {isBooked ? (
                      <>
                        <span>✓</span>
                        <span>Bokad</span>
                      </>
                    ) : isBooking ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                        <span>Bokar…</span>
                      </>
                    ) : (
                      'Boka denna tid'
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="italic text-gray-500 mb-4">Inga tider valda</div>
      )}

      <FollowupActions
        onSuggestTime={onSuggestTime}
        onBookMeeting={onBookMeeting}
        onApproveForCustomer={onApproveForCustomer}
        onBookHomeVisit={onBookHomeVisit}
        approveLoading={approveLoading}
        isApproved={isApproved}
      />
    </div>
  );
};

export default CustomerTimeSlots;
